import React from 'react';
import config from '../config';
import { useAuth } from '../auth/AuthContext';

const RealtimeContext = React.createContext(null);

const MAX_TRACKED_UPDATES = 250;

function resolveWsUrl(ticket) {
  const base = new URL(config.apiBaseUrl);
  base.protocol = base.protocol === 'https:' ? 'wss:' : 'ws:';
  const path = base.pathname.replace(/\/+$/, '') + '/realtime/ws';
  base.pathname = path;
  base.search = '';
  base.searchParams.set('ticket', ticket);
  return base.toString();
}

export const RealtimeProvider = ({ children }) => {
  const { isAuthenticated, fetchWithAuth } = useAuth();
  const socketRef = React.useRef(null);
  const reconnectTimerRef = React.useRef(null);
  const reconnectAttemptRef = React.useRef(0);
  const stopRef = React.useRef(false);

  const [connectionState, setConnectionState] = React.useState('idle');
  const [lastError, setLastError] = React.useState(null);
  const [jobUpdates, setJobUpdates] = React.useState([]);
  const [entityUpdates, setEntityUpdates] = React.useState([]);

  const closeSocket = React.useCallback(() => {
    if (socketRef.current) {
      socketRef.current.close();
      socketRef.current = null;
    }
  }, []);

  const scheduleReconnect = React.useCallback((connectFn) => {
    if (stopRef.current) {
      return;
    }
    reconnectAttemptRef.current += 1;
    const delay = Math.min(30000, 500 * Math.pow(2, reconnectAttemptRef.current));
    if (reconnectTimerRef.current) {
      window.clearTimeout(reconnectTimerRef.current);
    }
    reconnectTimerRef.current = window.setTimeout(() => {
      connectFn();
    }, delay);
  }, []);

  const connect = React.useCallback(async () => {
    if (!isAuthenticated || config.authDisabled || stopRef.current) {
      return;
    }

    setConnectionState('connecting');
    setLastError(null);

    try {
      const ticketResp = await fetchWithAuth(`${config.apiBaseUrl}/realtime/ticket`, {
        method: 'POST',
      });
      if (!ticketResp.ok) {
        const body = await ticketResp.json().catch(() => null);
        throw new Error(body?.error || 'failed to create realtime ticket');
      }
      const ticketData = await ticketResp.json();
      const ticket = ticketData?.ticket;
      if (!ticket) {
        throw new Error('missing realtime ticket');
      }

      const ws = new WebSocket(resolveWsUrl(ticket));
      socketRef.current = ws;

      ws.onopen = () => {
        reconnectAttemptRef.current = 0;
        setConnectionState('connected');
      };

      ws.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);
          if (payload?.type === 'job.updated') {
            setJobUpdates((prev) => {
              const next = [payload, ...prev];
              if (next.length > MAX_TRACKED_UPDATES) {
                return next.slice(0, MAX_TRACKED_UPDATES);
              }
              return next;
            });
            return;
          }
          if (payload?.type === 'entity.updated' || payload?.type === 'entity.deleted') {
            setEntityUpdates((prev) => {
              const next = [payload, ...prev];
              if (next.length > MAX_TRACKED_UPDATES) {
                return next.slice(0, MAX_TRACKED_UPDATES);
              }
              return next;
            });
          }
        } catch (err) {
          console.warn('invalid realtime payload', err);
        }
      };

      ws.onerror = () => {
        setConnectionState('error');
      };

      ws.onclose = () => {
        socketRef.current = null;
        if (stopRef.current) {
          setConnectionState('disconnected');
          return;
        }
        setConnectionState('disconnected');
        scheduleReconnect(connect);
      };
    } catch (err) {
      setConnectionState('error');
      setLastError(err?.message || 'realtime connection failed');
      scheduleReconnect(connect);
    }
  }, [fetchWithAuth, isAuthenticated, scheduleReconnect]);

  React.useEffect(() => {
    stopRef.current = false;
    if (!isAuthenticated || config.authDisabled) {
      closeSocket();
      setConnectionState('idle');
      return () => {
        stopRef.current = true;
      };
    }

    connect();

    return () => {
      stopRef.current = true;
      if (reconnectTimerRef.current) {
        window.clearTimeout(reconnectTimerRef.current);
      }
      closeSocket();
    };
  }, [isAuthenticated, connect, closeSocket]);

  const clearJobUpdates = React.useCallback(() => {
    setJobUpdates([]);
  }, []);

  const value = React.useMemo(
    () => ({
      connectionState,
      lastError,
      jobUpdates,
      entityUpdates,
      clearJobUpdates,
      latestJobUpdate: jobUpdates.length > 0 ? jobUpdates[0] : null,
      latestEntityUpdate: entityUpdates.length > 0 ? entityUpdates[0] : null,
    }),
    [clearJobUpdates, connectionState, entityUpdates, jobUpdates, lastError],
  );

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
};

export const useRealtime = () => {
  const ctx = React.useContext(RealtimeContext);
  if (!ctx) {
    throw new Error('useRealtime must be used within a RealtimeProvider');
  }
  return ctx;
};
